/*
  Node Handle Component

  Reusable handle component for connecting nodes in the journey graph.
  Provides consistent styling for hidden and visible handles.
*/

import React from 'react';
import { Handle, Position } from 'react-flow-renderer';

interface NodeHandleProps {
  type: 'source' | 'target';
  position?: 'left' | 'right' | 'top' | 'bottom';
  id?: string;
  hidden?: boolean;
  style?: React.CSSProperties;
}

/**
 * Standard hidden handle style used for most journey nodes
 */
const HIDDEN_HANDLE_STYLE: React.CSSProperties = {
  opacity: 0,
  pointerEvents: 'none',
  width: 1,
  height: 1,
};

/**
 * Position mapping for Handle component
 */
const POSITION_MAP: Record<string, Position> = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

/**
 * Reusable handle component that handles positioning and visibility
 */
const NodeHandle: React.FC<NodeHandleProps> = ({
  type,
  position = type === 'source' ? 'right' : 'left',
  id = type,
  hidden = true,
  style = {},
}) => {
  const handlePosition = POSITION_MAP[position] || Position.Right;

  // Calculate final style based on visibility
  const finalStyle: React.CSSProperties = hidden
    ? {
      ...HIDDEN_HANDLE_STYLE,
      [position]: -1,
      ...style,
    }
    : style;

  return (
    <Handle
      type={type}
      position={handlePosition}
      id={id}
      style={finalStyle}
    />
  );
};

export default NodeHandle;

